import React, {useEffect, useRef, useState} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {toast, ToastContainer} from 'react-toastify'
import {Form} from 'react-bootstrap'
import {TextInput} from '../../../../components/inputs/TextInput'
import {SelectInput} from '../../../../components/inputs/SelectInput'
import {ModeleClient} from '../../../../components/Models'
import {pays} from '../../../../components/Data'
import {checkClientContactDuplicate, getCompanyBySiret} from '../../../../components/requests'
import {KTSVG} from '../../../../_metronic/helpers'
import Swal from 'sweetalert2'

const yopSiret = (isRequired = true) => {
  let schema = Yup.string()
    .transform((value) => (value ? value.replace(/\s+/g, '') : ''))
    .test('only-digits', 'Le SIRET doit contenir uniquement des chiffres', (value) => {
      return /^\d*$/.test(value || '')
    })
    .matches(/^\d{14}$/, {
      message: 'Le SIRET doit contenir exactement 14 chiffres',
      excludeEmptyString: true,
    })

  return isRequired ? schema.required('Le SIRET est requis') : schema.notRequired()
}

const siretSchema = Yup.object({
  siret: yopSiret(), // Pas besoin de `.shape()` ici
})


const phoneRegex = /^(?:\+33|33|0)[\s.-]*[1-9](?:[\s.-]?\d{2}){4}$/

let clientSchemaSocietyProspect = Yup.object()
  .shape({
    professionnel: Yup.string().default('1'),
    nomSociete: Yup.string().required('Le nom de la société est requis'),
    siren: yopSiret(false),
    codeNaf: Yup.string().notRequired(),
    libelleCodeNaf: Yup.string().notRequired(),
    telephone: Yup.string()
      .nullable()
      .matches(phoneRegex, 'Format du numéro de téléphone invalide'),
    mobile: Yup.string().nullable().matches(phoneRegex, 'Format du mobile invalide'),
    email: Yup.string().nullable().email("L'adresse mail n'est pas valide"),
    siteWeb: Yup.string().notRequired(),
  })
  .test(
    'at-least-one-contact',
    'Au moins un des champs téléphone, mobile ou email doit être renseigné',
    function (value) {
      const {telephone, mobile, email} = value
      if (telephone || mobile || email) {
        return true
      }
      return this.createError({
        path: 'contactGroupError',
        message: 'Au moins un des champs téléphone, mobile ou email doit être renseigné',
      })
    }
  )

let clientSchemaSociety = Yup.object().shape({
  professionnel: Yup.string().default('1'),
  nomSociete: Yup.string().required('Le nom de la société est requis'),
  nom: Yup.string().required('Le nom est requis'),
  prenom: Yup.string().required('Le prénom est requis'),
  adresse: Yup.string().required("L'adresse est requise"),
  complementAdresse: Yup.string().notRequired(),
  codePostal: Yup.string().required('Le code postal est requis'),
  ville: Yup.string().required('La ville est requise'),
  siren: yopSiret(),
  codeNaf: Yup.string().required('Le code naf est requis'),
  libelleCodeNaf: Yup.string().required('Le libelle code naf est requis'),
  telephone: Yup.string().when(['mobile'], {
    is: (mobile) => !mobile || mobile.length < 1,
    then: () =>
      Yup.string()
        .required('Numéro de fixe ou mobile requis')
        .matches(phoneRegex, 'Format du numéro de téléphone invalide'),
    otherwise: () =>
      Yup.string().notRequired().matches(phoneRegex, 'Format du numéro de téléphone invalide'),
  }),
  mobile: Yup.string().notRequired().matches(phoneRegex, 'Format du mobile invalide'),
  email: Yup.string()
    .required("L'adresse mail est requise")
    .email("L'adresse mail n'est pas valide"),
  siteWeb: Yup.string().notRequired(),
})

let clientSchemaParticularProspect = Yup.object()
  .shape({
    professionnel: Yup.string().default('1'),
    nomSociete: Yup.string().when(['professionnel'], {
      is: (professionnel) =>
        professionnel === true ? true : Boolean(parseInt(professionnel.toString())),
      then: () => Yup.string().required('Le nom de la société est requis'),
      otherwise: () => Yup.string().notRequired(),
    }),
    codeNaf: Yup.string().notRequired(),
    libelleCodeNaf: Yup.string().notRequired(),
    nom: Yup.string().required('Le nom est requise'),
    prenom: Yup.string().required('Le prénom est requise'),
    adresse: Yup.string().notRequired(),
    complementAdresse: Yup.string().notRequired(),
    codePostal: Yup.string().notRequired(),
    ville: Yup.string().notRequired(),
    siren: Yup.string().notRequired(),
    siteWeb: Yup.string().notRequired(),
    telephone: Yup.string()
      .nullable()
      .matches(phoneRegex, 'Format du numéro de téléphone invalide'),
    mobile: Yup.string().nullable().matches(phoneRegex, 'Format du mobile invalide'),
    email: Yup.string().nullable().email("L'adresse mail n'est pas valide"),
  })
  .test(
    'at-least-one-contact',
    'Au moins un des champs téléphone, mobile ou email doit être renseigné',
    function (value) {
      const {telephone, mobile, email} = value
      if (telephone || mobile || email) {
        return true
      }
      return this.createError({
        path: 'contactGroupError',
        message: 'Au moins un des champs téléphone, mobile ou email doit être renseigné',
      })
    }
  )

let clientSchemaParticular = Yup.object().shape({
  professionnel: Yup.string().default('1'),
  nomSociete: Yup.string().when(['professionnel'], {
    is: (professionnel) =>
      professionnel === true ? true : Boolean(parseInt(professionnel.toString())),
    then: () => Yup.string().required('Le nom de la société est requis'),
    otherwise: () => Yup.string().notRequired(),
  }),
  codeNaf: Yup.string().notRequired(),
  libelleCodeNaf: Yup.string().notRequired(),
  nom: Yup.string().required('Le nom est requise'),
  prenom: Yup.string().required('Le prénom est requise'),
  adresse: Yup.string().required("L'adresse est requise"),
  complementAdresse: Yup.string().notRequired(),
  codePostal: Yup.string().required('Le code postal est requis'),
  ville: Yup.string().required('La ville est requise'),
  siren: Yup.string().notRequired(),
  telephone: Yup.string().when(['mobile'], {
    is: (mobile) => !mobile || mobile.length < 1,
    then: () =>
      Yup.string()
        .required('Numéro de fixe ou mobile requis')
        .matches(phoneRegex, 'Format du numéro de téléphone invalide'),
    otherwise: () => Yup.string().notRequired().matches(phoneRegex, 'Format du mobile invalide'),
  }),
  mobile: Yup.string().notRequired().matches(phoneRegex, 'Format du mobile invalide'),
  email: Yup.string()
    .required("L'adresse mail est requise")
    .email("L'adresse mail n'est pas valide"),
  siteWeb: Yup.string().notRequired(),
})

const FicheClientForm = (props) => {
  //initilisation model client proprieté api
  const isDataFilled = props.data && Object.values(props.data).some((v) => v !== '' && v !== null)
  const initialData = isDataFilled && props.data


  const [clientId, setClientId] = useState(props.clientId)
  const [data, setData] = useState<ModeleClient>(props.data)
  const [loadingSiret, setLoadingSiret] = useState(false)
  const [professional, setProfessional] = useState(props.data?.professionnel)
  const [useModal, setUseModal] = useState(false)
  const modalCreateCliRef = useRef<HTMLSpanElement | null>(null)
  const [modalExists, setModalExists] = useState(false)
  const [isProspectPro, setIsProspectPro] = useState(false)
  const {loading, setIsProspectChecked, isProspect, setIsProspect, convertClient, btnCloseSuggesteRef} = props

  const formik = useFormik<ModeleClient>({
    initialValues: data,
    enableReinitialize: true,
    validationSchema: () =>
      Yup.lazy(() => {
        const isPro = isProfessionel(formik.values.professionnel)

        if (convertClient) {
          return isPro ? clientSchemaSociety : clientSchemaParticular
        }

        if (isProspect) {
          return isPro ? clientSchemaSocietyProspect : clientSchemaParticularProspect
        }

        return isPro ? clientSchemaSociety : clientSchemaParticular
      }),

    onSubmit: async (values) => {
      const updatedData = { ...data, ...values }
      try {
        //  Vérifie les doublons
        const { emailExists, telephoneExists } = await checkClientContactDuplicate({
          id: updatedData.id,
          email: updatedData.email,
          telephone: updatedData.telephone,
        })
        let message = ''
        if (emailExists && telephoneExists) {
          message = 'Le mail et le numéro de téléphone saisis sont déjà utilisés par un autre client. Voulez-vous continuer ?'
        } else if (emailExists) {
          message = 'Le mail saisi est déjà utilisé par un autre client. Voulez-vous continuer ?'
        } else if (telephoneExists) {
          message = 'Le numéro du téléphone saisi est déjà utilisé par un autre client. Voulez-vous continuer ?'
        }
        //  Demande confirmation si doublon
        if (message) {
          const result = await Swal.fire({
            title: 'Oups',
            text: message,
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: 'Oui, continuer',
            cancelButtonText: 'Annuler',
            confirmButtonColor: '#1BC5BD',
            cancelButtonColor: '#F64E60',
          })
          if (!result.isConfirmed) {
            // setLoading(false)
            return
          }
        }
        //  Soumission du formulaire
        props.submitForm(updatedData, (response) => {
          formik.resetForm()
          // setLoading(false)
        })
      } catch (error) {
        console.error(error)
        // setLoading(false)
      }
    },
  })

  useEffect(() => {
    const size = Object.keys(props.data).length

    // Définir les champs vides par défaut
    const emptyFields: ModeleClient = {
      siren: '',
      nomSociete: '',
      nom: '',
      prenom: '',
      adresse: '',
      complementAdresse: '',
      codePostal: '',
      ville: '',
      telephone: '',
      mobile: '',
      email: '',
      siteWeb: '',
      codeNaf: '',
      libelleCodeNaf: '',
      professionnel: false, // valeur par défaut
    }

    // Fusionner avec props.data
    const merged = size > 2 ? props.data : { ...emptyFields, ...props.data }

    // S'assurer que `professionnel` est un booléen
    const professionnelValue =
      merged.professionnel === true || merged.professionnel === '1'

    setData({
      ...merged,
      professionnel: professionnelValue,
    })
  }, [props.data])

  // déclenchement du modal de recherche de véhicule après la création du client
  useEffect(() => {
    if (props.openModalResVeh) {
      modalCreateCliRef.current?.click()
    }
  }, [props?.openModalResVeh])

  useEffect(() => {
    //Recalcule les erreurs avec le nouveau validationSchema,
    //Supprime les erreurs qui ne sont plus valides.
    formik.validateForm()
  }, [professional, isProspect])

  const isProfessionel = (value) => {
    if (value === false || value === true) {
      return value
    }
    if (value === undefined || value === null) {
      return false
    }
    return Boolean(parseInt(value?.toString()))
  }

  // evènemment search siret
  const searchCustomerBySiren = async () => {
    const siren = formik.values.siren
    try {
      await siretSchema.validate({siret: siren})
      if (!siren) return false
      setLoadingSiret(true)
      try {
        const data = await getCompanyBySiret(siren)
        if (data) {
          const dataSiret = {
            siren: siren,
            nom: data[0]['nom'] ?? props.data.nom,
            prenom: data[0]['prenom'] ?? props.data.prenom,
            nomSociete: data[0]['nomSociete'],
            adresse: data[0]['adresse'],
            complementAdresse: data[0]['complementAdresse'],
            codePostal: data[0]['codePostal'],
            ville: data[0]['ville'],
            pays: data[0]['pays'],
            codeNaf: data[0]['codeNaf'],
            libelleCodeNaf: data[0]['libelleCodeNaf'],
            professionnel: true,
            idPays: data[0]['idPays'],
            telephone: formik.values.telephone,
            mobile: formik.values.mobile,
            email: formik.values.email,
            siteWeb: formik.values.siteWeb,
            prospect: formik.values.prospect,
          }

          formik.setValues(dataSiret)

          setLoadingSiret(false)
        } else {
          formik.setValues(initialData)
          console.log('Aucune donnée trouvée pour ce SIRET.')
        }
      } catch (error) {
        props.data.siren && formik.setFieldValue('siren', props.data.siren)
        toast.error("Le siret n'est pas valide", {autoClose: 4000})
        console.error('Erreur lors de la récupération des données :', error)
        setLoadingSiret(false)
      }
    } catch (errSiret) {}
  }

  useEffect(() => {
    // Vérifie si une modal avec l'ID "clientCreatedModal" est présente dans le DOM
    const modal = document.getElementById('ModalClientCreate')
    setModalExists(!!modal)
    setUseModal(props?.isCreateViaModal ?? false)
  }, [])

  // pour déterminer si c'est lors de la création de demande ou pas
  useEffect(() => {
    setUseModal(props?.isCreateViaModal ?? false)
  }, [props])

  useEffect(() => {
    if (isProspect) {
      setIsProspectChecked(true)
      if (professional) setIsProspectPro(true)
      else setIsProspectPro(false)
    } else {
      setIsProspectChecked(false)
    }
  }, [isProspect])

  useEffect(() => {
    formik.setFieldValue('prospect', false)
  }, [])

  const handleChangeProsp = (val) => {
    setIsProspect(!val)
    formik.setFieldValue('prospect', !val)
  }

  const handleSiretChange = (e) => {
    const rawValue = e.target.value
    // Supprimer tous les espaces
    const normalizedValue = rawValue.replace(/\s+/g, '')
    formik.setFieldValue('siren', normalizedValue)
  }


  return (
    <div className='modal-dialog modal-xl'>
      <div className='modal-content'>
        <div className='modal-header'>
          <div>
            <h5 className='modal-title my-4'>Modification du fiche client</h5>
            <span className='text-warning'>
              Veuillez remplir les champs requis (*) pour continuer l'opération
            </span>
          </div>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            data-bs-dismiss='modal'
            aria-label='Close'
            ref={btnCloseSuggesteRef}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon svg-icon-2x' />
          </div>
        </div>
        <div className='modal-body'>
          <form onSubmit={formik.handleSubmit} noValidate className='form'>
            <div className='row mb-6'>
              <div className='col-lg-4 col-form-label fw-bold fs-6'></div>
              <label
                className={`col-lg-1 col-form-label fw-bold fs-6 ${
                  isProspect ? 'text-gray-400' : 'text-primary'
                }`}
              >
                Client
              </label>
              <div className='col-lg-1 fv-row'>
                <div className='d-flex align-items-center mt-3'>
                  <Form.Check type='checkbox' className='mb-4 form-switch'>
                    <Form.Check.Input
                      className='me-2'
                      name={`prospect`}
                      checked={isProspect}
                      disabled={true}
                    />
                  </Form.Check>
                </div>
              </div>
              <label
                className={`col-lg-1 col-form-label fw-bold fs-6 ${
                  isProspect ? 'text-primary' : 'text-gray-400'
                }`}
              >
                Prospect
              </label>
            </div>
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Type de client</label>
              <div className='col-lg-8 fv-row'>
                <div className='d-flex align-items-center mt-3'>
                  <label className='form-check form-check-inline form-check-solid me-5'>
                    <input
                      className='form-check-input'
                      disabled={true}
                      type='radio'
                      value={0}
                      name={'professionnel'}
                      onChange={formik.handleChange}
                      checked={formik.values.professionnel == false}
                      onClick={() => {
                        setProfessional(false)
                        if (isProspect) setIsProspectPro(false)
                      }}
                    />
                    <span className='fw-bold ps-2 fs-6'>Particulier</span>
                  </label>

                  <label className='form-check form-check-inline form-check-solid'>
                    <input
                      className='form-check-input'
                      disabled={true}
                      type='radio'
                      value={1}
                      name={'professionnel'}
                      onChange={formik.handleChange}
                      checked={formik.values.professionnel == true}
                      onClick={() => {
                        setProfessional(true)
                        if (isProspect) setIsProspectPro(true)
                      }}
                    />
                    <span className='fw-bold ps-2 fs-6'>Société</span>
                  </label>
                </div>
              </div>
            </div>

            <div
              className='row mb-6'
              style={!isProfessionel(formik.values.professionnel) ? {display: 'none'} : {}}
            >
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Siret</label>

              <div className='col-lg-4 fv-row'>
                <TextInput
                  fieldName={'siren'}
                  inputPlaceholder={'SIRET'}
                  inputType={'text'}
                  inputClassName={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                  formik={formik}
                />
              </div>
              <div className='col-lg-4 fv-row'>
                <button
                  type='button'
                  className='btn btn-primary'
                  disabled={loadingSiret}
                  onClick={() => searchCustomerBySiren()}
                >
                  {!loadingSiret && 'Rechercher'}
                  {loadingSiret && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Recherche...{' '}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>

            <div
              className='row mb-6'
              style={!isProfessionel(formik.values.professionnel) ? {display: 'none'} : {}}
            >
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>Code naf</label>

              <div className='col-lg-8'>
                <div className='row'>
                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'codeNaf'}
                      inputPlaceholder={'Code naf'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>

                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'libelleCodeNaf'}
                      inputPlaceholder={'Libelle code naf'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='row mb-6'>
              <label
                className={`col-lg-4 col-form-label ${
                  isProspect ? (!isProspectPro ? 'required' : '') : 'required'
                } fw-bold fs-6`}
              >
                Nom complet
              </label>

              <div className='col-lg-8'>
                <div className='row'>
                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'nom'}
                      inputPlaceholder={'Nom'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>

                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'prenom'}
                      inputPlaceholder={'Prenom'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              className='row mb-6'
              style={!isProfessionel(formik.values.professionnel) ? {display: 'none'} : {}}
            >
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                Nom de la société
              </label>

              <div className='col-lg-8 fv-row'>
                <TextInput
                  fieldName={'nomSociete'}
                  inputPlaceholder={'Société'}
                  inputType={'text'}
                  inputClassName={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                  formik={formik}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label
                className={`col-lg-4 col-form-label ${isProspect ? '' : 'required'} fw-bold fs-6`}
              >
                Adresse principale
              </label>
              <div className='col-lg-8'>
                <div className='row mb-4'>
                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'adresse'}
                      inputPlaceholder={'Adresse'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>

                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'complementAdresse'}
                      inputPlaceholder={"Complément d'adresse"}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>
                </div>
                <div className='row mb-4'>
                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'codePostal'}
                      inputPlaceholder={'Code postal'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>

                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'ville'}
                      inputPlaceholder={'Ville'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>
                </div>
                <div className='row'>
                  <div className='col-lg-12 fv-row'>
                    <SelectInput
                      fieldName={'idPays'}
                      inputClassName={'form-select form-select-solid form-select-lg fw-bold'}
                      options={pays}
                      formik={formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='row mb-6'>
              <label
                className={`col-lg-4 col-form-label ${isProspect ? '' : 'required'} fw-bold fs-6`}
              >
                Téléphone
              </label>

              <div className='col-lg-8'>
                <div className='row'>
                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'telephone'}
                      inputPlaceholder={'Fixe'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>

                  <div className='col-lg-6 fv-row'>
                    <TextInput
                      fieldName={'mobile'}
                      inputPlaceholder={'Mobile'}
                      inputType={'text'}
                      inputClassName={
                        'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                      }
                      formik={formik}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='row mb-6'>
              <label
                className={`col-lg-4 col-form-label ${isProspect ? '' : 'required'} fw-bold fs-6`}
              >
                Email
              </label>

              <div className='col-lg-8 fv-row'>
                <TextInput
                  fieldName={'email'}
                  inputPlaceholder={'Email'}
                  inputType={'text'}
                  inputClassName={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                  formik={formik}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>Site web</label>

              <div className='col-lg-8 fv-row'>
                <TextInput
                  fieldName={'siteWeb'}
                  inputPlaceholder={'Site web'}
                  inputType={'text'}
                  inputClassName={'form-control form-control-lg form-control-solid mb-3 mb-lg-0'}
                  formik={formik}
                />
              </div>
            </div>
            {formik.errors.contactGroupError && isProspect && (
              <div className='row mb-6'>
                <div className='col-lg-4'></div>
                <div className='col-lg-8 form-error fv-plugins-message-container'>
                  <div className='fv-help-block'>{formik.errors.contactGroupError}</div>
                </div>
              </div>
            )}
            {/*</div>*/}

            <div className='modal-footer'>
              {clientId !== null && (
                <button
                  type='button'
                  className='btn btn-light me-3'
                  disabled={loading}
                  onClick={() => {
                    formik.resetForm()
                  }}
                  data-bs-dismiss='modal'
                >
                  Annuler les modifications
                </button>
              )}
              <button
                type='submit'
                className={`btn btn-primary`}
                disabled={loading}
              >
                {!loading && (formik.dirty ? 'Enregistrer et continuer' : 'Continuer')}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {props.statusText}...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export {FicheClientForm}
